/**
 * The last runs of an automation as bars, as in the canvas automation
 * block's health footer (`automation-canvas-block.ts`: same count, heights
 * and colours), so the sidebar's footer reads like the block's.
 */
import type { FC } from 'react';

import type { ExecutionLog } from '../../services/logging';
import * as styles from './run-bars.css';

const RUN_BAR_COUNT = 12;
const RUN_BAR_MIN_HEIGHT = 5;
const RUN_BAR_MAX_HEIGHT = 16;

type RunBarStatus = 'completed' | 'failed' | 'cancelled' | 'running';

const durationOf = (log: ExecutionLog) =>
  log.completedAt ? log.completedAt - log.startedAt : 0;

export const RunBars: FC<{
  /** Runs, newest first; a running one is drawn last at full height. */
  logs: readonly ExecutionLog[];
  /** Label per bar (date and outcome), for its tooltip. */
  describe: (log: ExecutionLog) => string;
}> = ({ logs, describe }) => {
  const active = logs.find(
    log => log.status === 'running' || log.status === 'awaiting_external'
  );
  const runs = logs
    .filter(
      log =>
        log.status === 'completed' ||
        log.status === 'failed' ||
        log.status === 'cancelled'
    )
    .slice(0, RUN_BAR_COUNT)
    .reverse();
  if (runs.length === 0 && !active) return null;

  const maxDuration = Math.max(0, ...runs.map(durationOf));
  const heightOf = (log: ExecutionLog) => {
    const duration = durationOf(log);
    if (!duration || maxDuration === 0) return RUN_BAR_MIN_HEIGHT + 4;
    return (
      RUN_BAR_MIN_HEIGHT +
      (duration / maxDuration) * (RUN_BAR_MAX_HEIGHT - RUN_BAR_MIN_HEIGHT)
    );
  };
  const bar = (log: ExecutionLog, status: RunBarStatus, height: number) => (
    <i
      key={log.id}
      className={styles.runBar}
      data-status={status}
      style={{ height }}
      title={describe(log)}
    />
  );

  return (
    <span className={styles.runBars} data-testid="automation-run-bars">
      {runs.map(log => bar(log, log.status as RunBarStatus, heightOf(log)))}
      {active && bar(active, 'running', RUN_BAR_MAX_HEIGHT)}
    </span>
  );
};
